import clsx from "clsx";
import { format } from "date-fns";
import { useEffect, useState } from "react";

import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/core/Drawer";
import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import { useFileManagerMount } from "@/components/features/FileManager/FileManagerMount";
import { formatFileSize } from "@/utils/formatFileSize";
import type { FileManagerItem } from "@/components/core/FileManager/FileManager.types";

type ItemTag = FileManagerItem["tags"][number];

function registryContextRows(item: FileManagerItem): Array<{ label: string; value: string }> {
  const context = item.context as
    | {
        entityName?: string;
        clientName?: string;
        workOrderNumber?: string;
        conversationTitle?: string;
        senderName?: string;
      }
    | undefined;
  if (!context) return [];
  const rows: Array<{ label: string; value: string }> = [];
  if (context.entityName) rows.push({ label: "Entity", value: context.entityName });
  if (context.clientName) rows.push({ label: "Client", value: context.clientName });
  if (context.workOrderNumber) rows.push({ label: "Work Order", value: context.workOrderNumber });
  if (context.conversationTitle) rows.push({ label: "Chat", value: context.conversationTitle });
  if (context.senderName) rows.push({ label: "Sent By", value: context.senderName });
  return rows;
}

import styles from "./DetailsModal.module.scss";
import { Badge, Button } from "@onengine/component-library";
import { IconCheck, IconCircleX, IconExternalLink, IconPlus } from "@tabler/icons-react";
import {
  isSystemTag,
  parseOriginTag,
  ORIGIN_TAG_PREFIX,
  SYSTEM_TAG_APPROVED,
} from "@/components/features/FileManager/fileTags.utils";

type DrawerTab = "details" | "activity";

export interface ActivityEntry {
  id: string;
  timestamp: string;
  actionLabel: string;
  source?: string;
  preposition?: string;
  destination?: string;
  user: {
    name: string;
    avatarUrl?: string;
  };
}

function getFileTypeLabel(item: FileManagerItem): string {
  if (item.contentType) {
    const sub = item.contentType.split("/")[1];
    return (sub ?? item.contentType).toUpperCase();
  }
  const ext = item.name.split(".").pop();
  return ext ? ext.toUpperCase() : "—";
}

function DetailDateTime({ isoString }: { isoString: string }) {
  const dt = new Date(isoString);
  const date = format(dt, "dd MMM, yyyy");
  const time = format(dt, "h:mm a");
  return (
    <div className={styles.detailDateTime}>
      <span className={styles.detailDatePart}>{date}</span>
      <span className={styles.detailDateSep} aria-hidden />
      <span className={styles.detailTimePart}>{time}</span>
    </div>
  );
}

function DetailsTab({ item }: { item: FileManagerItem }) {
  const { updateTags, updateNote, isUpdatingNote, openFolder, permissions } = useFileManager();
  const { browseItems } = useFileManagerMount();
  const { openTags, closeDetails } = useFileManagerUI();
  const [localTags, setLocalTags] = useState<ItemTag[]>(item.tags);
  const [localNote, setLocalNote] = useState(item.note ?? "");
  const [savedNote, setSavedNote] = useState<{
    text: string | null;
    updatedAt: string | null;
    updatedByName: string | null;
  }>({
    text: item.note ?? null,
    updatedAt: item.noteUpdatedAt ?? null,
    updatedByName: item.noteUpdatedByName ?? null,
  });

  useEffect(() => {
    setLocalTags(item.tags);
  }, [item.id]);

  useEffect(() => {
    setLocalNote(item.note ?? "");
    setSavedNote({
      text: item.note ?? null,
      updatedAt: item.noteUpdatedAt ?? null,
      updatedByName: item.noteUpdatedByName ?? null,
    });
  }, [item.id, item.note, item.noteUpdatedAt, item.noteUpdatedByName]);

  const uploadedBy = item.uploadedBy as { name: string; avatarUrl?: string } | undefined;

  const userTags = localTags.filter((t) => !isSystemTag(t.name));
  const isApproved = localTags.some((t) => t.name === SYSTEM_TAG_APPROVED);
  const originTagName = localTags.find((t) => t.name.startsWith(ORIGIN_TAG_PREFIX))?.name;
  const originInfo = originTagName ? parseOriginTag(originTagName) : null;

  function removeTag(tagId: string) {
    const prev = localTags;
    const updated = prev.filter((t) => t.id !== tagId);
    setLocalTags(updated);
    updateTags(
      { id: item.id, name: item.name, tags: updated.map((t) => t.name) },
      { onSuccess: () => {}, onError: () => setLocalTags(prev) },
    );
  }

  async function handleOriginClick() {
    if (!originInfo) return;
    const rootItems = await browseItems(null, null);
    const folder = rootItems.find((f) => f.type === "FOLDER" && f.name === originInfo.folderName);
    if (!folder) return;
    closeDetails();
    openFolder(folder.id, folder.name, [{ id: folder.id, name: folder.name }]);
  }

  function saveNote() {
    const noteValue = localNote.trim() || null;
    updateNote({ id: item.id, note: noteValue }, { onSuccess: () => {}, onError: () => {} });
  }

  const noteIsDirty = localNote.trim() !== (savedNote.text ?? "").trim();

  return (
    <div className={styles.detailsSection}>
      <p className={styles.detailsSectionTitle}>File Info</p>
      <div className={styles.detailsItems}>
        {item.type === "FILE" && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>File Type</span>
            <span className={styles.detailValueSemibold}>{getFileTypeLabel(item)}</span>
          </div>
        )}
        {item.fileSize != null && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>File Size</span>
            <span className={styles.detailValue}>{formatFileSize(item.fileSize)}</span>
          </div>
        )}
        {uploadedBy && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>File Uploaded By</span>
            <div className={styles.detailUserValue}>
              {uploadedBy.avatarUrl ? (
                <img
                  src={uploadedBy.avatarUrl}
                  alt={uploadedBy.name}
                  className={styles.detailAvatar}
                />
              ) : (
                <div className={styles.detailAvatar} aria-hidden />
              )}
              <span className={styles.detailValueMedium}>{uploadedBy.name}</span>
            </div>
          </div>
        )}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>File Created</span>
          <DetailDateTime isoString={item.createdAt} />
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>File Last Modified</span>
          <DetailDateTime isoString={item.updatedAt} />
        </div>
        {registryContextRows(item).map(({ label, value }) => (
          <div key={label} className={styles.detailRow}>
            <span className={styles.detailLabel}>{label}</span>
            <span className={styles.detailValue}>{value}</span>
          </div>
        ))}
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Tags</span>
          <div className={styles.detailTagsWrap}>
            {isApproved && (
              <Badge
                variant="outline"
                color="success"
                size="large"
                iconLeft={<IconCheck size={14} />}
              >
                Approved
              </Badge>
            )}
            {originInfo && (
              <button
                type="button"
                className={styles.originTag}
                onClick={handleOriginClick}
                title={`Go to origin folder: ${originInfo.folderName}`}
              >
                <IconExternalLink size={14} />
                {originInfo.folderName}
              </button>
            )}
            {userTags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                color="neutral"
                size="large"
                iconRight={
                  permissions.canEdit ? (
                    <Button
                      variant="text-secondary"
                      onClick={() => removeTag(tag.id)}
                      aria-label={`Remove tag ${tag.name}`}
                    >
                      <IconCircleX size={16} />
                    </Button>
                  ) : undefined
                }
              >
                {tag.name}
              </Badge>
            ))}
            {permissions.canEdit && (
              <Button
                variant="text-secondary"
                onClick={() =>
                  openTags({ id: item.id, name: item.name, tags: userTags.map((t) => t.name) })
                }
              >
                <IconPlus /> New tag
              </Button>
            )}
          </div>
        </div>
      </div>

      {permissions.canEdit && (
        <div className={styles.noteSection}>
          <p className={styles.detailsSectionTitle}>
            Note
            {savedNote.updatedAt && (
              <span className={styles.noteEdited}>
                Edited {format(new Date(savedNote.updatedAt), "dd MMM, yyyy")}
                {savedNote.updatedByName ? ` by ${savedNote.updatedByName}` : ""}
              </span>
            )}
          </p>
          <textarea
            className={styles.noteTextarea}
            value={localNote}
            onChange={(e) => setLocalNote(e.target.value)}
            placeholder="Add an internal note..."
            rows={4}
          />
          <div className={styles.noteActions}>
            <Button
              variant="secondary"
              size="small"
              disabled={!noteIsDirty || isUpdatingNote}
              onClick={saveNote}
            >
              {isUpdatingNote ? "Saving..." : "Save note"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityTimeline({ entries }: { entries: ActivityEntry[] }) {
  if (entries.length === 0) {
    return <p className={styles.emptyState}>No activity recorded yet.</p>;
  }

  return (
    <div className={styles.timeline}>
      {entries.map((entry, index) => {
        const dt = new Date(entry.timestamp);
        const dateStr = dt.toLocaleDateString();
        const timeStr = dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

        return (
          <div key={entry.id} className={styles.timelineEntry}>
            <div className={styles.timelineIndicator}>
              <div className={styles.timelineDot} />
              {index < entries.length - 1 && <div className={styles.timelineConnector} />}
            </div>
            <div className={styles.timelineContent}>
              <div className={styles.timelineDate}>
                <span>{dateStr}</span>
                <span className={styles.timelineDateSep} aria-hidden />
                <span>{timeStr}</span>
              </div>
              <div className={styles.timelineAction}>
                <span className={styles.timelineActionText}>{entry.actionLabel}</span>
                {entry.source && <span className={styles.timelineHighlight}>{entry.source}</span>}
                {entry.preposition && (
                  <span className={styles.timelineActionText}>{entry.preposition}</span>
                )}
                {entry.destination && (
                  <span className={styles.timelineHighlight}>{entry.destination}</span>
                )}
              </div>
              <div className={styles.timelineUser}>
                {entry.user.avatarUrl ? (
                  <img
                    src={entry.user.avatarUrl}
                    alt={entry.user.name}
                    className={styles.timelineAvatar}
                  />
                ) : (
                  <div className={styles.timelineAvatar} aria-hidden />
                )}
                <span className={styles.timelineUsername}>{entry.user.name}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface DetailsModalProps {
  activities?: ActivityEntry[];
}

export function DetailsModal({ activities = [] }: DetailsModalProps) {
  const { detailsTarget, closeDetails } = useFileManagerUI();
  const [activeTab, setActiveTab] = useState<DrawerTab>("details");

  useEffect(() => {
    if (detailsTarget) setActiveTab("details");
  }, [detailsTarget?.id]);

  return (
    <Drawer
      open={!!detailsTarget}
      onOpenChange={(open) => {
        if (!open) closeDetails();
      }}
      swipeDirection="right"
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>{detailsTarget?.name}</DrawerTitle>
          <DrawerClose />
        </DrawerHeader>

        <div className={styles.tabBar}>
          <button
            type="button"
            className={clsx(styles.tab, activeTab === "details" && styles.active)}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            type="button"
            className={clsx(styles.tab, activeTab === "activity" && styles.active)}
            onClick={() => setActiveTab("activity")}
          >
            Activity
          </button>
        </div>

        <DrawerBody>
          {activeTab === "details" && detailsTarget && <DetailsTab item={detailsTarget} />}
          {activeTab === "activity" && (
            <div className={styles.activitySection}>
              <p className={styles.activitySectionTitle}>Logs</p>
              <ActivityTimeline entries={activities} />
            </div>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
